import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/customerProductPricing"

import {
  GetCustomerProductPricingDocument,
  type GetCustomerProductPricingQuery,
  PriceChangeType,
} from "@/graphql/codegen/graphql"

const CUSTOMER_PRICING_LIMIT = 100

type GetSuccess = Extract<
  GetCustomerProductPricingQuery["priceChange"]["getPriceChangeItems"],
  { __typename: "GetPriceChangeItemsSuccess" }
>
export type CustomerPricingGroup = GetSuccess["priceChangeItems"][number]["groups"][number]
export type CustomerPricingItem = CustomerPricingGroup["items"][number]

export interface ExistingSpecialRow {
  item: CustomerPricingItem
  shipFromWarehouseArea: CustomerPricingGroup["shipFromWarehouseArea"]
  incoterms: CustomerPricingGroup["incoterms"]
  incotermsLocation: CustomerPricingGroup["incotermsLocation"]
}

interface UseGetCustomerPricingParams {
  productId: number | undefined
  customerId: string | undefined
  shipFromWarehouseAreaId: string | undefined
}

interface UseGetCustomerPricingReturn {
  listGroups: CustomerPricingGroup[]
  specialGroups: CustomerPricingGroup[]
  applicableSpecials: ExistingSpecialRow[]
  isFetching: boolean
}

interface PartitionedPricingGroups {
  listGroups: CustomerPricingGroup[]
  specialGroups: CustomerPricingGroup[]
}

export const partitionGroupItems = (groups: CustomerPricingGroup[]): PartitionedPricingGroups => {
  const listGroups: CustomerPricingGroup[] = []
  const specialGroups: CustomerPricingGroup[] = []

  for (const group of groups) {
    const listItems = group.items.filter((item) => item.customerId === null)
    const specialItems = group.items.filter((item) => item.customerId !== null)
    if (listItems.length) listGroups.push({ ...group, items: listItems })
    if (specialItems.length) specialGroups.push({ ...group, items: specialItems })
  }

  return { listGroups, specialGroups }
}

// Caller passes specialGroups only; rows inherit their group's (shipFrom, incoterms, location).
const groupToRows = (group: CustomerPricingGroup): ExistingSpecialRow[] =>
  group.items.map((item) => ({
    item,
    shipFromWarehouseArea: group.shipFromWarehouseArea,
    incoterms: group.incoterms,
    incotermsLocation: group.incotermsLocation,
  }))

export const useGetCustomerPricing = ({
  productId,
  customerId,
  shipFromWarehouseAreaId,
}: UseGetCustomerPricingParams): UseGetCustomerPricingReturn => {
  const pause = !productId || !customerId || !shipFromWarehouseAreaId

  const [{ data, fetching }] = useQuery<GetCustomerProductPricingQuery>({
    query: GetCustomerProductPricingDocument,
    variables: {
      input: {
        filter: {
          changeType: PriceChangeType.SellingPrice,
          customerIds: customerId ? [customerId] : undefined,
          specificProductIds: productId ? [productId] : undefined,
          shipFromId: shipFromWarehouseAreaId ?? undefined,
        },
        offset: 0,
        limit: CUSTOMER_PRICING_LIMIT,
      },
    },
    pause,
  })

  return useMemo(() => {
    const output = data?.priceChange.getPriceChangeItems
    if (output?.__typename !== "GetPriceChangeItemsSuccess") {
      return { listGroups: [], specialGroups: [], applicableSpecials: [], isFetching: fetching }
    }
    const allGroups = output.priceChangeItems.flatMap((priceChangeItem) => priceChangeItem.groups)
    const { listGroups, specialGroups } = partitionGroupItems(allGroups)
    const applicableSpecials: ExistingSpecialRow[] = specialGroups.flatMap(groupToRows)
    return { listGroups, specialGroups, applicableSpecials, isFetching: fetching }
  }, [data, fetching])
}
